import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, RotateCcw, KeyRound, BellRing } from "lucide-react";
import { toast } from "sonner";

const SEZIONI_COLLAB = [
  { tid: "nav-corsi", label: "Corsi" },
  { tid: "nav-materie", label: "Materie" },
  { tid: "nav-classi", label: "Classi" },
  { tid: "nav-calendario", label: "Calendario Lezioni" },
  { tid: "nav-test", label: "Area Test" },
  { tid: "nav-presenze", label: "Registro Presenze" },
  { tid: "nav-anagrafiche", label: "Anagrafiche" },
  { tid: "nav-materiali", label: "Materiali Didattici" },
  { tid: "nav-impostazioni", label: "Impostazioni" },
  { tid: "nav-registro-pagamenti", label: "Registrazione Pagamenti (economico)" },
  { tid: "nav-solleciti", label: "Solleciti (economico)" },
  { tid: "nav-iscrizioni", label: "Pre-iscrizioni (economico)" },
  { tid: "nav-corsi-pubblici", label: "Corsi Pubblici (economico)" },
];

function ComuneSelect({ label, value, onChange, tid, onProvincia, initialProv, onCap }) {
  const [prov, setProv] = useState([]);
  const [sigla, setSigla] = useState(initialProv || "");
  const [comuni, setComuni] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/geo/province").then(r=>setProv(r.data)).catch(()=>{}); }, []);
  useEffect(() => {
    if (!sigla) { setComuni([]); return; }
    api.get(`/geo/comuni?provincia=${sigla}`).then(r=>setComuni(r.data)).catch(()=>{});
  }, [sigla]);
  const filtered = comuni.filter(c => !q || c.nome.toLowerCase().includes(q.toLowerCase())).slice(0, 300);
  return (
    <div className="col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
      <div><Label>{label} — Provincia</Label>
        <Select value={sigla} onValueChange={(v)=>{ setSigla(v); if (onProvincia) onProvincia(v); }}>
          <SelectTrigger data-testid={`${tid}-prov`}><SelectValue placeholder="Provincia"/></SelectTrigger>
          <SelectContent className="max-h-72">{prov.map(p=><SelectItem key={p.sigla} value={p.sigla}>{p.nome} ({p.sigla})</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div><Label>{label} — Comune</Label>
        <Select value={value||""} onValueChange={(v)=>{ onChange(v); if (onCap) { const cc = comuni.find(x=>x.nome===v); onCap((cc?.cap && cc.cap[0]) || ""); } }} disabled={!sigla}>
          <SelectTrigger data-testid={`${tid}-comune`}><SelectValue placeholder={value || (sigla ? "Seleziona comune" : "Prima la provincia")}/></SelectTrigger>
          <SelectContent className="max-h-72">
            <div className="p-1 sticky top-0 bg-white z-10"><Input value={q} onChange={e=>setQ(e.target.value)} onKeyDown={e=>e.stopPropagation()} placeholder="Cerca comune..." className="h-8" data-testid={`${tid}-comune-search`}/></div>
            {filtered.map(c=><SelectItem key={c.nome} value={c.nome}>{c.nome}</SelectItem>)}
            {filtered.length === 0 && <div className="px-2 py-3 text-xs text-stone-400 text-center">Nessun comune</div>}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

function StatoNascita({ value, onChange, tid }) {
  const [stati, setStati] = useState([]);
  const [q, setQ] = useState("");
  useEffect(()=>{ api.get("/geo/stati").then(r=>setStati(r.data)).catch(()=>{}); }, []);
  const filtered = stati.filter(s=>!q || s.toLowerCase().includes(q.toLowerCase())).slice(0,300);
  return (
    <div className="col-span-2"><Label>Stato di nascita</Label>
      <Select value={value||"Italia"} onValueChange={onChange}>
        <SelectTrigger data-testid={`${tid}-stato`}><SelectValue placeholder="Stato di nascita"/></SelectTrigger>
        <SelectContent className="max-h-72">
          <div className="p-1 sticky top-0 bg-white z-10"><Input value={q} onChange={e=>setQ(e.target.value)} onKeyDown={e=>e.stopPropagation()} placeholder="Cerca stato..." className="h-8" data-testid={`${tid}-stato-search`}/></div>
          {filtered.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

export default function AnagrafichePage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [studenti, setStudenti] = useState([]);
  const [docenti, setDocenti] = useState([]);
  const [open, setOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [corsi, setCorsi] = useState([]);
  const [collaboratori, setCollaboratori] = useState([]);
  const [materieAnag, setMaterieAnag] = useState([]);
  const [form, setForm] = useState({ name:"", email:"", role:"studente", materie:[], corso_id:"", classe_id:"", sesso:"", prezzo_personalizzato:0, cognome:"", codice_fiscale:"", data_nascita:"", luogo_nascita:"", indirizzo:"", citta:"", cap:"", telefono:"", provincia:"", provincia_nascita:"", stato_nascita:"Italia" });
  const [classiAnag, setClassiAnag] = useState([]);

  const load = async () => {
    try {
      const [s, d, c, ma, cl] = await Promise.all([api.get("/users?role=studente"), api.get("/users?role=docente"), api.get("/corsi"), api.get("/materie"), api.get("/classi")]);
      setStudenti(s.data); setDocenti(d.data); setCorsi(c.data); setMaterieAnag(ma.data); setClassiAnag(cl.data);
      if (isAdmin) { const co = await api.get("/users?role=collaboratore"); setCollaboratori(co.data); }
    } catch { toast.error("Errore caricamento anagrafiche"); }
  };
  useEffect(() => { load(); }, []);

  const errMsg = (e) => {
    const d = e.response?.data?.detail;
    if (Array.isArray(d)) return d.map(x => (x.msg || "").replace("Value error, ", "")).join(" · ") || "Errore di validazione";
    return d || "Errore";
  };

  const [search, setSearch] = useState("");
  const [pages, setPages] = useState({});
  const [stato, setStato] = useState("attivi");
  const applyFilters = (list) => list.filter(u => {
    const q = search.trim().toLowerCase();
    const matchQ = !q || `${u.name} ${u.cognome||""} ${u.email} ${u.codice_fiscale||""}`.toLowerCase().includes(q);
    const matchS = stato === "tutti" || (stato === "disattivati" ? u.disattivato : !u.disattivato);
    return matchQ && matchS;
  });

  const delUser = async (u) => {
    if (!window.confirm(`Disattivare ${u.name}? L'utente non potrà più accedere ma resterà recuperabile da qui (tasto Riattiva).`)) return;
    try {
      const { data } = await api.delete(`/users/${u.id}`);
      if (data.disattivato) toast.info(data.message);
      else toast.success(data.message || "Utente eliminato");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const riattiva = async (u) => {
    try { await api.post(`/users/${u.id}/riattiva`); toast.success("Utente riattivato"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const [cred, setCred] = useState(null);
  const [solleciti, setSolleciti] = useState(null); // {studente, items} | null
  const openSolleciti = async (u) => {
    try {
      const { data } = await api.get(`/solleciti/studente/${u.id}`);
      setSolleciti({ studente: u, items: data });
    } catch (e) { toast.error(errMsg(e)); }
  };
  const resetPwd = async (u) => {
    if (!window.confirm(`Reimpostare la password di ${u.name}? Le sessioni attive verranno chiuse e la nuova password inviata via email.`)) return;
    try {
      const { data } = await api.post(`/users/${u.id}/reset-password`);
      setCred({ nome: u.name, email: u.email, password: data.password });
      toast.success("Password reimpostata e inviata");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const putUser = async () => {
    await api.put(`/users/${editUser.id}`, { name: editUser.name, role: editUser.role, materie: editUser.materie || [], corso_id: editUser.corso_id || "", prezzo_personalizzato: +(editUser.prezzo_personalizzato || 0),
        cognome: editUser.cognome || "", codice_fiscale: editUser.codice_fiscale || "", data_nascita: editUser.data_nascita || "", luogo_nascita: editUser.luogo_nascita || "", provincia: editUser.provincia || "", provincia_nascita: editUser.provincia_nascita || "", stato_nascita: editUser.stato_nascita || "Italia",
        indirizzo: editUser.indirizzo || "", citta: editUser.citta || "", cap: editUser.cap || "", telefono: editUser.telefono || "",
        sesso: editUser.sesso || "", ...(editUser.email ? { email: editUser.email } : {}), ...(editUser.role === "studente" ? { classe_id: editUser.classe_id || "" } : {}),
        sezioni_consentite: editUser.role === "collaboratore" ? (editUser.sezioni_consentite || []) : null,
        classi_consentite: editUser.role === "collaboratore" ? (editUser.classi_consentite || []) : null });
  };
  const saveEdit = async () => {
    try {
      await putUser();
      toast.success("Utente aggiornato"); setEditUser(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const saveEReinvia = async () => {
    if (!editUser.email) { toast.error("Inserisci un'email valida prima di reinviare le credenziali"); return; }
    if (!window.confirm(`Correggere l'email e reinviare le credenziali a ${editUser.email}? Verrà generata una nuova password e le sessioni attive verranno chiuse.`)) return;
    try {
      await putUser();
      const { data } = await api.post(`/users/${editUser.id}/reset-password`);
      setCred({ nome: editUser.name, email: data.email, password: data.password });
      toast.success("Email aggiornata e nuove credenziali inviate");
      setEditUser(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const generaCF = async (f, setF) => {
    try {
      const { data } = await api.post("/users/genera-cf", { nome:f.name, cognome:f.cognome, sesso:f.sesso, data_nascita:f.data_nascita, luogo_nascita:f.luogo_nascita, stato_nascita:f.stato_nascita });
      setF({ ...f, codice_fiscale: data.codice_fiscale });
      toast.success("Codice fiscale generato");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const renderAnagrafica = (f, setF, tid) => (
    <>
      <div className="col-span-2 text-[11px] font-bold uppercase tracking-[0.15em] text-[#1F4E79] mt-1 border-b border-[#E2E8F0] pb-1">Dati anagrafici</div>
      <div><Label>Sesso</Label>
        <Select value={f.sesso||""} onValueChange={v=>setF({...f,sesso:v})}>
          <SelectTrigger data-testid={`${tid}-sesso`}><SelectValue placeholder="M / F"/></SelectTrigger>
          <SelectContent><SelectItem value="M">M</SelectItem><SelectItem value="F">F</SelectItem></SelectContent>
        </Select>
      </div>
      <div><Label>Data di nascita</Label><Input type="date" data-testid={`${tid}-nascita`} value={f.data_nascita||""} onChange={e=>setF({...f,data_nascita:e.target.value})}/></div>
      <StatoNascita value={f.stato_nascita} onChange={(v)=>setF(prev=>({...prev, stato_nascita:v, ...(v==="Italia"?{luogo_nascita:"", provincia_nascita:""}:{luogo_nascita:v, provincia_nascita:"EE"})}))} tid={tid}/>
      {(!f.stato_nascita || f.stato_nascita==="Italia")
        ? <ComuneSelect label="Luogo di nascita" value={f.luogo_nascita} onChange={v=>setF({...f,luogo_nascita:v})} onProvincia={v=>setF({...f,provincia_nascita:v})} initialProv={f.provincia_nascita} tid={`${tid}-luogo`}/>
        : <div className="col-span-2 text-xs text-stone-600 bg-[#F5F3EF] rounded-md p-2">Nato/a all'estero: luogo di nascita impostato su <b>{f.luogo_nascita}</b>. Il codice fiscale userà il codice dello Stato estero.</div>}
      <div><Label>Codice Fiscale (estero ammesso)</Label>
        <div className="flex gap-1">
          <Input data-testid={`${tid}-cf`} maxLength={20} value={f.codice_fiscale||""} onChange={e=>setF({...f,codice_fiscale:e.target.value.toUpperCase()})}/>
          <Button type="button" variant="outline" size="sm" onClick={()=>generaCF(f,setF)} data-testid={`${tid}-genera-cf`}>Genera</Button>
        </div>
      </div>
      <div className="col-span-2"><Label>Indirizzo</Label><Input data-testid={`${tid}-indirizzo`} value={f.indirizzo||""} onChange={e=>setF({...f,indirizzo:e.target.value})}/></div>
      <ComuneSelect label="Città (residenza)" value={f.citta} onChange={v=>setF({...f,citta:v})} onProvincia={v=>setF({...f,provincia:v})} onCap={v=>setF(prev=>({...prev, cap:v}))} initialProv={f.provincia} tid={`${tid}-citta`}/>
      <div><Label>CAP</Label><Input data-testid={`${tid}-cap`} maxLength={5} value={f.cap||""} onChange={e=>setF({...f,cap:e.target.value})}/></div>
      <div className="col-span-2 text-[11px] font-bold uppercase tracking-[0.15em] text-[#1F4E79] mt-1 border-b border-[#E2E8F0] pb-1">Contatti e consensi</div>
      <div className="col-span-2"><Label>Telefono</Label><Input data-testid={`${tid}-telefono`} value={f.telefono||""} onChange={e=>setF({...f,telefono:e.target.value})} placeholder="+39 ..."/></div>
    </>
  );

  const save = async () => {
    try {
      const { data } = await api.post("/users", form);
      toast.success(data.email ? "Utente creato — credenziali inviate via email" : "Utente creato — username generato"); setOpen(false); load();
      if (data.password_iniziale) setCred({ nome: form.name, email: data.login_id || form.email, password: data.password_iniziale });
      setForm({ name:"", email:"", role:"studente", materie:[], corso_id:"", classe_id:"", sesso:"", prezzo_personalizzato:0, cognome:"", codice_fiscale:"", data_nascita:"", luogo_nascita:"", indirizzo:"", citta:"", cap:"", telefono:"", provincia:"", provincia_nascita:"", stato_nascita:"Italia" });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const renderTable = (rows, roleLabel) => {
    const filtered = applyFilters(rows);
    const PER = 20;
    const pg = pages[roleLabel] || 1;
    const totPages = Math.max(1, Math.ceil(filtered.length / PER));
    const pageRows = filtered.slice((pg - 1) * PER, pg * PER);
    const setPg = (n) => setPages(p => ({ ...p, [roleLabel]: n }));
    return (
    <Card className="p-4 bg-white border-[#E2E8F0]">
      <Table>
        <TableHeader><TableRow><TableHead>Nome</TableHead><TableHead>Email</TableHead><TableHead>{roleLabel === "Docente" ? "Materie" : "Classe abbinata"}</TableHead><TableHead>Ruolo</TableHead><TableHead>Iscritto dal</TableHead><TableHead className="text-right">Azioni</TableHead></TableRow></TableHeader>
        <TableBody>
          {pageRows.map(u => {
            const classeAbb = classiAnag.find(c => (c.studenti_ids || []).includes(u.id));
            return (
            <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
              <TableCell className="font-medium">{u.name}{u.cognome ? ` ${u.cognome}` : ""}{u.disattivato && <Badge className="ml-2 bg-stone-200 text-stone-600 border-stone-300">Disattivato</Badge>}{u.codice_fiscale && <span className="block text-xs text-stone-400 font-mono">{u.codice_fiscale}</span>}</TableCell>
              <TableCell>{u.email || u.username}{u.telefono && <span className="block text-xs text-stone-500">{u.telefono}{u.citta ? ` · ${u.citta}${u.provincia ? " ("+u.provincia+")" : ""}` : ""}</span>}</TableCell>
              <TableCell className="text-sm">{roleLabel === "Docente" ? (u.materie?.length ? u.materie.join(", ") : "-") : (<>{classeAbb?.nome || "-"}{u.prezzo_personalizzato ? <span className="block text-xs text-[#F7941E]">€ {u.prezzo_personalizzato} personalizzato</span> : null}</>)}</TableCell>
              <TableCell><Badge className="bg-[#FFF5EB] text-[#C96F0E] border-[#F5D9B8]">{roleLabel}</Badge></TableCell>
              <TableCell className="text-sm text-stone-500">{u.created_at?.slice(0,10)}</TableCell>
              <TableCell className="text-right">
                <Button variant="ghost" size="sm" onClick={()=>setEditUser({...u, materie: u.materie||[], corso_id: u.corso_id||"", sesso: u.sesso||"", classe_id: classiAnag.find(c=>(c.studenti_ids||[]).includes(u.id))?.id || ""})} data-testid={`edit-user-${u.id}`}><Pencil className="w-4 h-4 text-[#F7941E]"/></Button>
                <Button variant="ghost" size="sm" onClick={()=>resetPwd(u)} title="Reimposta password" data-testid={`reset-pwd-${u.id}`}><KeyRound className="w-4 h-4 text-[#1F4E79]"/></Button>
                {roleLabel === "Studente" && (
                  <Button variant="ghost" size="sm" onClick={()=>openSolleciti(u)} title="Storico solleciti" data-testid={`storico-solleciti-${u.id}`}><BellRing className="w-4 h-4 text-[#C96F0E]"/></Button>
                )}
                {u.disattivato ? (
                  <Button variant="ghost" size="sm" onClick={()=>riattiva(u)} title="Riattiva utente" data-testid={`riattiva-user-${u.id}`}><RotateCcw className="w-4 h-4 text-emerald-700"/></Button>
                ) : (
                  <Button variant="ghost" size="sm" onClick={()=>delUser(u)} title="Elimina utente" data-testid={`del-user-${u.id}`}><Trash2 className="w-4 h-4 text-red-500"/></Button>
                )}
              </TableCell>
            </TableRow>
          );})}
          {filtered.length===0 && <TableRow><TableCell colSpan={6} className="text-center text-stone-500">Nessuno.</TableCell></TableRow>}
        </TableBody>
      </Table>
      {filtered.length > PER && (
        <div className="flex items-center justify-between mt-3 text-sm text-stone-600" data-testid={`paginazione-${roleLabel}`}>
          <span>{(pg-1)*PER+1}-{Math.min(pg*PER, filtered.length)} di {filtered.length}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={pg<=1} onClick={()=>setPg(pg-1)} data-testid={`pag-prec-${roleLabel}`}>Precedente</Button>
            <span className="px-2 py-1">Pag. {pg}/{totPages}</span>
            <Button variant="outline" size="sm" disabled={pg>=totPages} onClick={()=>setPg(pg+1)} data-testid={`pag-succ-${roleLabel}`}>Successiva</Button>
          </div>
        </div>
      )}
    </Card>
    );
  };

  return (
    <div className="p-4 sm:p-8">
      <div className="flex flex-wrap gap-4 items-center justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#F7941E] mb-2">Anagrafiche</div>
          <h1 className="text-3xl font-bold text-stone-900">Utenti</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-[#F7941E] hover:bg-[#E08316] text-white" data-testid="new-user-btn"><Plus className="w-4 h-4 mr-2"/>Nuovo Utente</Button></DialogTrigger>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Nuovo Utente</DialogTitle><DialogDescription>Crea un account con ruolo, materie (docente) o corso abbinato (studente).</DialogDescription></DialogHeader>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="col-span-2 text-[11px] font-bold uppercase tracking-[0.15em] text-[#1F4E79] border-b border-[#E2E8F0] pb-1">Dati di accesso</div>
              <div><Label>Nome</Label><Input data-testid="new-user-nome" value={form.name} onChange={e=>setForm({...form,name:e.target.value})}/></div>
              <div><Label>Cognome</Label><Input data-testid="new-user-cognome" value={form.cognome||""} onChange={e=>setForm({...form,cognome:e.target.value})}/></div>
              <div className="col-span-2"><Label>Email (facoltativa — se vuota verrà generato un username nome.cognome)</Label><Input type="email" data-testid="user-email-input" value={form.email} onChange={e=>setForm({...form,email:e.target.value})}/></div>
              <div className="col-span-2 text-sm text-stone-500 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg px-3 py-2">Se non inserisci l'email, il sistema genera un <b>username</b> (es. mario.rossi) per l'accesso. La password iniziale è generata automaticamente e la vedrai dopo la creazione.</div>
              {renderAnagrafica(form, setForm, "new-user")}
              <div><Label>Ruolo</Label>
                <Select value={form.role} onValueChange={v=>setForm({...form,role:v})}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent><SelectItem value="studente">Studente</SelectItem><SelectItem value="docente">Docente</SelectItem>{isAdmin && <SelectItem value="collaboratore">Collaboratore</SelectItem>}{isAdmin && <SelectItem value="admin">Admin</SelectItem>}</SelectContent>
                </Select>
              </div>
              {form.role === "docente" && (
                <div><Label>Materie insegnate</Label>
                  <div className="border border-[#E2E8F0] rounded-lg max-h-40 overflow-y-auto p-2 space-y-1" data-testid="user-materie-list">
                    {materieAnag.map(m => (
                      <label key={m.id} className="flex items-center gap-2 p-1 hover:bg-[#FFF5EB] rounded cursor-pointer">
                        <input type="checkbox" checked={(form.materie||[]).includes(m.nome)} data-testid={`user-materia-${m.id}`} onChange={()=>{const cur=form.materie||[]; setForm({...form, materie: cur.includes(m.nome)?cur.filter(x=>x!==m.nome):[...cur,m.nome]});}}/>
                        <span className="text-sm">{m.nome}</span>
                      </label>
                    ))}
                    {materieAnag.length===0 && <div className="text-xs text-[#C53030]">Crea prima le materie nella sezione Materie.</div>}
                  </div>
                </div>
              )}
              {form.role === "studente" && (
                <div><Label>Classe abbinata</Label>
                  <Select value={form.classe_id} onValueChange={v=>setForm({...form,classe_id:v})}>
                    <SelectTrigger data-testid="user-classe-select"><SelectValue placeholder="Seleziona classe"/></SelectTrigger>
                    <SelectContent>{classiAnag.map(c=><SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {form.role === "studente" && (
                <div><Label>Prezzo personalizzato € (0 = prezzo standard del corso)</Label><Input type="number" data-testid="user-prezzo-input" value={form.prezzo_personalizzato} onChange={e=>setForm({...form,prezzo_personalizzato:+e.target.value})}/></div>
              )}
            </div>
            <DialogFooter><Button onClick={save} className="bg-[#F7941E] hover:bg-[#E08316] text-white" data-testid="user-save-btn">Crea</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex flex-wrap gap-3 mb-4 items-center">
        <div className="min-w-[280px] flex-1 max-w-md">
          <Input placeholder="Cerca per nome, email o codice fiscale..." value={search} onChange={e=>setSearch(e.target.value)} data-testid="anagrafiche-search"/>
        </div>
        <div className="min-w-[180px]">
          <Select value={stato} onValueChange={setStato}>
            <SelectTrigger data-testid="anagrafiche-stato-filter"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="tutti">Tutti gli stati</SelectItem>
              <SelectItem value="attivi">Solo attivi</SelectItem>
              <SelectItem value="disattivati">Solo disattivati</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Tabs defaultValue="studenti">
        <TabsList className="bg-[#F5F3EF]">
          <TabsTrigger value="studenti" data-testid="tab-studenti">Studenti ({studenti.length})</TabsTrigger>
          <TabsTrigger value="docenti" data-testid="tab-docenti">Docenti ({docenti.length})</TabsTrigger>
          {isAdmin && <TabsTrigger value="collaboratori" data-testid="tab-collaboratori">Collaboratori ({collaboratori.length})</TabsTrigger>}
        </TabsList>
        <TabsContent value="studenti" className="mt-4">{renderTable(studenti, "Studente")}</TabsContent>
        <TabsContent value="docenti" className="mt-4">{renderTable(docenti, "Docente")}</TabsContent>
        {isAdmin && <TabsContent value="collaboratori" className="mt-4">{renderTable(collaboratori, "Collaboratore")}</TabsContent>}
      </Tabs>

      <Dialog open={!!editUser} onOpenChange={(o)=>{if(!o) setEditUser(null);}}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Modifica utente</DialogTitle><DialogDescription>Aggiorna nome, materie o corso abbinato dell'utente selezionato.</DialogDescription></DialogHeader>
          {editUser && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><Label>Nome</Label><Input data-testid="edit-user-name" value={editUser.name} onChange={e=>setEditUser({...editUser,name:e.target.value})}/></div>
            <div><Label>Cognome</Label><Input data-testid="edit-user-cognome" value={editUser.cognome||""} onChange={e=>setEditUser({...editUser,cognome:e.target.value})}/></div>
            {isAdmin && (
              <div className="col-span-2"><Label>Ruolo</Label>
                <Select value={editUser.role} onValueChange={v=>setEditUser({...editUser, role:v})}>
                  <SelectTrigger data-testid="edit-user-role"><SelectValue/></SelectTrigger>
                  <SelectContent><SelectItem value="studente">Studente</SelectItem><SelectItem value="docente">Docente</SelectItem><SelectItem value="collaboratore">Collaboratore</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
                </Select>
              </div>
            )}
            <div className="col-span-2">
              <Label>Email {editUser.username ? "(vuota = accesso con username)" : "(usata per l'accesso e le comunicazioni)"}</Label>
              <Input type="email" data-testid="edit-user-email" value={editUser.email || ""} onChange={e=>setEditUser({...editUser, email:e.target.value})} placeholder={editUser.username ? `Nessuna email — username: ${editUser.username}` : "nome@dominio.it"}/>
              <div className="text-xs text-stone-500 mt-1">Correggi l'email in caso di errore, poi usa "Salva e reinvia credenziali" per rimandare l'accesso.</div>
            </div>
            {renderAnagrafica(editUser, setEditUser, "edit-user")}
            {editUser.role === "docente" && (
              <div><Label>Materie insegnate</Label>
                <div className="border border-[#E2E8F0] rounded-lg max-h-40 overflow-y-auto p-2 space-y-1" data-testid="edit-user-materie-list">
                  {materieAnag.map(m => (
                    <label key={m.id} className="flex items-center gap-2 p-1 hover:bg-[#FFF5EB] rounded cursor-pointer">
                      <input type="checkbox" checked={(editUser.materie||[]).includes(m.nome)} onChange={()=>{const cur=editUser.materie||[]; setEditUser({...editUser, materie: cur.includes(m.nome)?cur.filter(x=>x!==m.nome):[...cur,m.nome]});}}/>
                      <span className="text-sm">{m.nome}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            {editUser.role === "studente" && (
              <div><Label>Classe abbinata</Label>
                <Select value={editUser.classe_id || ""} onValueChange={v=>setEditUser({...editUser,classe_id:v})}>
                  <SelectTrigger data-testid="edit-user-classe"><SelectValue placeholder="Seleziona classe"/></SelectTrigger>
                  <SelectContent>{classiAnag.map(c=><SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {editUser.role === "studente" && (
              <div><Label>Prezzo personalizzato €</Label><Input type="number" data-testid="edit-user-prezzo" value={editUser.prezzo_personalizzato||0} onChange={e=>setEditUser({...editUser,prezzo_personalizzato:+e.target.value})}/></div>
            )}
            {editUser.role === "collaboratore" && (
              <div className="col-span-2">
                <Label className="flex items-center gap-1">Sezioni visibili al collaboratore</Label>
                <div className="text-xs text-stone-500 mb-2">Se non selezioni nulla, il collaboratore vede tutte le sezioni consentite (esclusa la parte economica). La Dashboard è sempre visibile.</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 border border-[#E2E8F0] rounded-lg p-2">
                  {SEZIONI_COLLAB.map(s => {
                    const sel = (editUser.sezioni_consentite || []).includes(s.tid);
                    return (
                      <label key={s.tid} className="flex items-center gap-2 p-1.5 hover:bg-[#FFF5EB] rounded cursor-pointer">
                        <input type="checkbox" checked={sel} data-testid={`sezione-${s.tid}`} onChange={()=>{
                          const cur = editUser.sezioni_consentite || [];
                          setEditUser({...editUser, sezioni_consentite: sel ? cur.filter(x=>x!==s.tid) : [...cur, s.tid]});
                        }}/>
                        <span className="text-sm">{s.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
            {editUser.role === "collaboratore" && (
              <div className="col-span-2">
                <Label className="flex items-center gap-1">Classi visibili al collaboratore</Label>
                <div className="text-xs text-stone-500 mb-2">Se non selezioni nulla, il collaboratore vede tutte le classi. Altrimenti vedrà solo quelle scelte (e i dati collegati).</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 border border-[#E2E8F0] rounded-lg p-2 max-h-40 overflow-y-auto">
                  {classiAnag.map(c => {
                    const sel = (editUser.classi_consentite || []).includes(c.id);
                    return (
                      <label key={c.id} className="flex items-center gap-2 p-1.5 hover:bg-[#FFF5EB] rounded cursor-pointer">
                        <input type="checkbox" checked={sel} data-testid={`classe-consentita-${c.id}`} onChange={()=>{
                          const cur = editUser.classi_consentite || [];
                          setEditUser({...editUser, classi_consentite: sel ? cur.filter(x=>x!==c.id) : [...cur, c.id]});
                        }}/>
                        <span className="text-sm">{c.nome}</span>
                      </label>
                    );
                  })}
                  {classiAnag.length === 0 && <div className="text-xs text-stone-400 p-1">Nessuna classe disponibile</div>}
                </div>
              </div>
            )}
          </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={saveEReinvia} data-testid="edit-user-save-reinvia">Salva e reinvia credenziali</Button>
            <Button onClick={saveEdit} className="bg-[#F7941E] hover:bg-[#E08316] text-white" data-testid="edit-user-save">Salva modifiche</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!solleciti} onOpenChange={(o)=>{if(!o) setSolleciti(null);}}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Storico solleciti — {solleciti?.studente?.name}{solleciti?.studente?.cognome ? ` ${solleciti.studente.cognome}` : ""}</DialogTitle><DialogDescription>Elenco dei solleciti di pagamento inviati a questo studente, dal più recente.</DialogDescription></DialogHeader>
          {solleciti && (
            solleciti.items.length === 0 ? (
              <div className="text-center text-sm text-stone-500 py-8" data-testid="solleciti-empty">Nessun sollecito inviato a questo studente.</div>
            ) : (
              <div className="space-y-2" data-testid="solleciti-list">
                {solleciti.items.map(s => (
                  <div key={s.id} className="border border-[#E2E8F0] rounded-lg p-3 bg-[#F8FAFC]" data-testid={`sollecito-row-${s.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-semibold text-[#0F2B48]">{s.corso_titolo}</div>
                      <div className="text-xs text-stone-500">{s.data ? new Date(s.data).toLocaleString("it-IT", {day:"2-digit",month:"2-digit",year:"numeric",hour:"2-digit",minute:"2-digit"}) : "-"}</div>
                    </div>
                    <div className="text-sm text-stone-600 mt-1">
                      {s.rata_numero != null ? <>Rata n. {s.rata_numero} · € {Number(s.rata_importo||0).toFixed(2)} · scadenza {s.rata_scadenza}</> : "Rata non disponibile"}
                      {s.rata_pagata && <Badge className="ml-2 bg-emerald-50 text-emerald-800 border-emerald-200">Regolarizzata</Badge>}
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!cred} onOpenChange={(o)=>{if(!o) setCred(null);}}>
        <DialogContent>
          <DialogHeader><DialogTitle>Credenziali di accesso — {cred?.nome}</DialogTitle><DialogDescription>Inviate via email. Copiale ora: la password non sarà più visibile dopo la chiusura.</DialogDescription></DialogHeader>
          {cred && (
            <div className="bg-[#FFF5EB] border border-[#F5D9B8] rounded-lg p-4 font-mono text-sm break-all" data-testid="credenziali-box">
              <div>Accesso: {cred.email}</div>
              <div>Password: {cred.password}</div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={async ()=>{ try { const r = await api.post("/users/credenziali-pdf", { nome: cred.nome, email: cred.email, password: cred.password }, { responseType: "blob" }); const link=document.createElement("a"); link.href=URL.createObjectURL(r.data); link.download=`credenziali_${cred.email}.pdf`; link.click(); } catch { toast.error("Errore download PDF"); } }} data-testid="scarica-credenziali-pdf-btn">Scarica PDF</Button>
            <Button variant="outline" onClick={()=>{navigator.clipboard.writeText(`Email: ${cred.email}\nPassword: ${cred.password}`); toast.success("Credenziali copiate negli appunti");}} data-testid="copia-credenziali-btn">Copia credenziali</Button>
            <Button onClick={()=>setCred(null)} className="bg-[#F7941E] hover:bg-[#E08316] text-white" data-testid="chiudi-credenziali-btn">Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
